import { optimizedApiService } from './optimizedApiService';
import { performanceOptimizer } from '../utils/performanceOptimizer';
import { API_URL } from '../config';

/**
 * 搜索服务
 * 用于处理全站搜索功能
 */
class SearchService {
  /**
   * 执行搜索
   * @param {string} query - 搜索关键词
   * @param {Object} options - 搜索选项
   * @param {number} options.page - 页码
   * @param {number} options.pageSize - 每页数量
   * @param {Array<string>} options.types - 搜索类型，可选值：article, weekly, idea, private-stash
   * @returns {Promise<Object>} 搜索结果
   */
  async search(query, options = {}) {
    const {
      page = 1,
      pageSize = 10,
      types = ['article', 'weekly', 'idea', 'private-stash']
    } = options;

    try {
      // 构建查询参数
      const params = {
        query,
        page,
        pageSize,
        types: types.join(',')
      };

      // 检查是否是标签搜索
      if (query.startsWith('tag:')) {
        const tagName = query.substring(4); // 移除 'tag:' 前缀
        params.tag = tagName;
        params.query = ''; // 清空普通搜索查询
      }

      // 使用缓存键标识此搜索请求
      const cacheKey = `search:${query}:${page}:${pageSize}:${types.join(',')}`;
      const cachedResult = performanceOptimizer.getCachedResponse(cacheKey);
      
      if (cachedResult) {
        return cachedResult;
      }

      // 发送搜索请求
      const response = await optimizedApiService.api.get('/api/search', { params });
      
      // 处理搜索结果
      const results = this.processSearchResults(response.data.data);
      
      // 缓存结果（短时间，因为搜索结果可能会变化）
      performanceOptimizer.cacheResponse(cacheKey, results, 2 * 60 * 1000); // 2分钟缓存
      
      return results;
    } catch (error) {
      console.error('Search error:', error);
      throw error;
    }
  }

  /**
   * 处理搜索结果
   * @param {Object} data - 搜索结果数据
   * @returns {Object} 处理后的搜索结果
   */
  processSearchResults(data) {
    // 整合所有结果到一个数组中
    const allResults = [
      ...data.articles.map(item => ({
        ...item,
        coverUrl: item.coverUrl ? this.optimizeImageUrl(item.coverUrl, 'article') : null
      })),
      ...data.weeklies.map(item => ({
        ...item,
        coverUrl: item.coverUrl ? this.optimizeImageUrl(item.coverUrl, 'weekly') : null
      })),
      ...data.ideas,
      ...data.stashes.map(item => ({
        ...item,
        coverUrl: item.coverUrl ? this.optimizeImageUrl(item.coverUrl, 'stash') : null
      }))
    ];

    return {
      results: allResults,
      total: data.total,
      page: data.page,
      pageSize: data.pageSize,
      pageCount: data.pageCount,
      // 按类型分组的结果
      byType: {
        articles: data.articles,
        weeklies: data.weeklies,
        ideas: data.ideas,
        stashes: data.stashes
      }
    };
  }

  /**
   * 优化图片URL
   * @param {string} url - 原始URL
   * @param {string} type - 内容类型
   * @returns {string} 优化后的URL
   */
  optimizeImageUrl(url, type) {
    // 根据不同类型设置不同的尺寸
    const options = {
      article: { width: 200, height: 120 },
      weekly: { width: 200, height: 120 },
      stash: { width: 200, height: 120 }
    };

    // 处理URL，确保它是完整的URL
    const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
    
    return performanceOptimizer.optimizeImageUrl(fullUrl, options[type] || {});
  }

  /**
   * 获取搜索建议
   * @param {string} query - 搜索关键词
   * @returns {Promise<Array<string>>} 搜索建议
   */
  async getSuggestions(query) {
    if (!query || query.length < 2) {
      return [];
    }

    try {
      // 简单实现，实际可能需要更复杂的逻辑或专门的API
      const response = await this.search(query, {
        page: 1,
        pageSize: 5,
        types: ['article', 'weekly']
      });

      // 从结果中提取标题作为建议
      const suggestions = [
        ...response.byType.articles.map(item => item.title),
        ...response.byType.weeklies.map(item => item.title)
      ].slice(0, 5);

      return suggestions;
    } catch (error) {
      console.error('Error getting search suggestions:', error);
      return [];
    }
  }
}

// 创建单例
export const searchService = new SearchService();
export default searchService;